import { useState } from 'react';
import type { ReactElement } from 'react';
import { Laptop } from 'lucide-react';
import { formatDate } from '@xdocs/i18n';
import type { SessionPublic } from '@xdocs/schemas';
import { Badge, Button, ConfirmDialog, EmptyState, ListRow, Skeleton, cn, useI18n, useToast } from '@xdocs/ui';
import { useLogout } from '../../../ws/useLogout.js';
import { SettingsNav } from '../SettingsNav.js';
import type { SessionRow } from './useSessions.js';
import { useSessions } from './useSessions.js';
import { useSettingsSessionsT } from './useSettingsSessionsT.js';
import styles from './SessionsPage.module.css';

const SKELETON_ROW_COUNT = 3;

/** `/settings/sessions` (F13): list logged-in sessions, revoke one or all others. */
export function SessionsPage(): ReactElement {
  const { locale } = useI18n();
  const { t } = useSettingsSessionsT();
  const toast = useToast();
  const logout = useLogout();
  const { sessions, isLoading, isError, onRetry, revoke, isRevoking, revokeAllOthers, isRevokingAll } = useSessions();
  const [revokeTarget, setRevokeTarget] = useState<SessionRow | null>(null);
  const [revokeError, setRevokeError] = useState<string | undefined>(undefined);
  const [revokeAllOpen, setRevokeAllOpen] = useState(false);
  const [revokeAllError, setRevokeAllError] = useState<string | undefined>(undefined);

  async function handleRevoke(): Promise<void> {
    if (!revokeTarget) return;
    setRevokeError(undefined);
    try {
      const wasCurrent = revokeTarget.isCurrent;
      await revoke(revokeTarget.id);
      setRevokeTarget(null);
      if (wasCurrent) {
        await logout();
        return;
      }
      toast.push({ tone: 'success', message: t('settingsSessions:toast.revoked.action') });
    } catch {
      setRevokeError(t('settingsSessions:toast.revokeFailed'));
    }
  }

  async function handleRevokeAll(): Promise<void> {
    setRevokeAllError(undefined);
    try {
      await revokeAllOthers();
      setRevokeAllOpen(false);
      toast.push({ tone: 'success', message: t('settingsSessions:toast.revokedAll.action') });
    } catch {
      setRevokeAllError(t('settingsSessions:toast.revokeAllFailed'));
    }
  }

  const otherSessionsCount = sessions.filter((s) => !s.isCurrent).length;

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <h1 className={cn(styles['title'])}>{t('settingsSessions:page.title')}</h1>
        <p className={cn(styles['description'])}>{t('settingsSessions:page.description')}</p>
      </div>

      <SettingsNav />

      {isLoading && (
        <div aria-busy="true" className={cn(styles['list'])}>
          <Skeleton shape="row" count={SKELETON_ROW_COUNT} />
        </div>
      )}

      {isError && (
        <EmptyState
          kind="error"
          title={t('settingsSessions:status.error.title')}
          action={{ label: t('settingsSessions:action.retry'), onClick: onRetry }}
        />
      )}

      {!isLoading && !isError && sessions.length === 0 && (
        <EmptyState kind="no-results" title={t('settingsSessions:status.empty.title')} description={t('settingsSessions:status.empty.description')} />
      )}

      {!isLoading && !isError && sessions.length > 0 && (
        <>
          <ul className={cn(styles['list'])}>
            {sessions.map((session) => (
              <li key={session.id}>
                <ListRow
                  icon={<Laptop size={18} aria-hidden="true" />}
                  title={
                    <span className={cn(styles['rowTitle'])}>
                      {describeUserAgent(session.userAgent)}
                      {session.isCurrent && <Badge tone="success">{t('settingsSessions:session.current')}</Badge>}
                    </span>
                  }
                  meta={
                    <span className={cn(styles['rowMeta'])}>
                      {session.ip ?? '—'} ·{' '}
                      {t('settingsSessions:session.lastSeen', { when: formatDate(locale, session.lastSeenAt, { dateStyle: 'medium', timeStyle: 'short' }) })}
                      {session.isCurrent && (
                        <span className={cn(styles['currentWarning'])}>{t('settingsSessions:revoke.confirm.current.description')}</span>
                      )}
                    </span>
                  }
                  trailing={
                    <Button variant="ghost" size="sm" onClick={() => setRevokeTarget(session)}>
                      {t('settingsSessions:action.revoke')}
                    </Button>
                  }
                />
              </li>
            ))}
          </ul>

          {otherSessionsCount > 0 && (
            <div className={cn(styles['footerActions'])}>
              <Button variant="secondary" onClick={() => setRevokeAllOpen(true)}>
                {t('settingsSessions:action.revokeAll')}
              </Button>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        action={t('settingsSessions:action.revoke')}
        assetName={revokeTarget ? describeUserAgent(revokeTarget.userAgent) : ''}
        destructive
        loading={isRevoking}
        {...(revokeError ? { error: revokeError } : {})}
        onConfirm={() => void handleRevoke()}
      />

      <ConfirmDialog
        open={revokeAllOpen}
        onOpenChange={setRevokeAllOpen}
        action={t('settingsSessions:action.revokeAll')}
        assetName={t('settingsSessions:revokeAll.confirm.assetName', { count: otherSessionsCount })}
        destructive
        loading={isRevokingAll}
        {...(revokeAllError ? { error: revokeAllError } : {})}
        onConfirm={() => void handleRevokeAll()}
      />
    </div>
  );
}

function describeUserAgent(userAgent: SessionPublic['userAgent']): string {
  return userAgent ?? '—';
}
